import { Drawer } from "@circleco/compass/components/Drawer";
import { Icon } from "@circleco/compass/components/Icon";
import type { IconName } from "@circleco/compass/components/Icon";
import { Typography } from "@circleco/compass/components/Typography";
import type { ReactNode } from "react";

import { DRAWER_ITEM_BUTTON_CLASS } from "./drawerItemStyles";

interface DrawerMenuItemProps {
  /** Render the label in the danger color (e.g. "Sign out"). */
  danger?: boolean;
  /** Render a non-interactive, greyed-out row (no Drawer.Close wrapper). */
  disabled?: boolean;
  icon?: IconName;
  /** Don't dismiss the drawer on tap (e.g. a row that opens a popover). */
  keepOpen?: boolean;
  label: string;
  onClick?: () => void;
  /** Optional secondary line beneath the label. */
  subtitle?: string;
  /** Optional trailing element (e.g. a chevron or checkmark). */
  trailing?: ReactNode;
  /**
   * Optional interactive element (e.g. an IconButton) rendered beside the
   * row, outside its button, since a button can't be nested in a button.
   */
  trailingAction?: ReactNode;
}

/**
 * A single tappable row inside a mobile shell drawer: leading icon,
 * label (with optional subtitle), and optional trailing element.
 *
 * Enabled rows are wrapped in `Drawer.Close` so tapping dismisses the
 * drawer (unless `keepOpen`); disabled rows render as an inert, greyed-out button.
 */
export const DrawerMenuItem = ({
  icon,
  keepOpen = false,
  label,
  onClick,
  danger = false,
  disabled = false,
  subtitle,
  trailing,
  trailingAction,
}: DrawerMenuItemProps) => {
  let labelColor: "tertiary" | "danger" | undefined;
  if (disabled) {
    labelColor = "tertiary";
  } else if (danger) {
    labelColor = "danger";
  }

  const body = (
    <>
      {icon && <Icon name={icon} size="md" color={labelColor} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <Typography variant="body-md" color={labelColor}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="label-sm" color="tertiary">
            {subtitle}
          </Typography>
        )}
      </div>
      {trailing}
    </>
  );

  if (disabled) {
    return (
      <button type="button" className={DRAWER_ITEM_BUTTON_CLASS} disabled>
        {body}
      </button>
    );
  }

  const button = (
    <button
      type="button"
      className={DRAWER_ITEM_BUTTON_CLASS}
      onClick={onClick}
    >
      {body}
    </button>
  );
  const row = keepOpen ? button : <Drawer.Close>{button}</Drawer.Close>;

  if (!trailingAction) return row;

  return (
    <div className="flex items-center gap-1 pr-2">
      <div className="min-w-0 flex-1">{row}</div>
      {trailingAction}
    </div>
  );
};
